<template>
  <div class="ml-12 mr-12 my-2 livlife-users-container">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <!-- Page Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="pa-4" style="border-radius: 15px; background: linear-gradient(135deg, #002060 0%, #001547 100%);">
          <h2 class="text-white mb-2">Liv-Life Portal Users</h2>
          <p class="text-white text-opacity-80 mb-0">
            View all registered users from the Liv-Life client portal system
          </p>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Row -->
    <v-row class="mb-4">
      <!-- Search input -->
      <v-col cols="12" sm="6" md="4">
        <v-text-field
          v-model="search"
          label="Search Users"
          clearable
          class="elevation-0"
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          append-inner-icon="mdi-magnify"
          variant="solo"
        />
      </v-col>

      <!-- Status filter -->
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          label="Filter by Status"
          clearable
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          variant="solo"
        />
      </v-col>

      <!-- Refresh button -->
      <v-col cols="12" sm="6" md="3" class="d-flex align-center justify-start">
        <v-btn
          color="#002060"
          class="text-white"
          @click="loadUsers"
          prepend-icon="mdi-refresh"
          rounded
          flat
          :loading="loading"
        >
          Refresh Data
        </v-btn>
      </v-col>
    </v-row>

    <!-- Users Table -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="filteredUsers"
          :items-per-page="15"
          :loading="loading"
          style="border-radius: 17px;"
          loading-text="Loading users... Please wait"
          class="custom-header elevation-3"
          :footer-props="{ 'items-per-page-options': [10, 15, 25, 50, 100] }"
        >
          <!-- Member ID Column -->
          <template v-slot:item.memberId="{ item }">
            <span class="font-weight-medium">{{ item.memberId || 'N/A' }}</span>
          </template>

          <!-- Client ID Column -->
          <template v-slot:item.clientId="{ item }">
            <span class="font-weight-medium">{{ item.clientId || 'N/A' }}</span>
          </template>

          <!-- Name Column -->
          <template v-slot:item.name="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="32" color="#002060" class="mr-2">
                <v-icon size="20" color="white">mdi-account</v-icon>
              </v-avatar>
              <span class="font-weight-medium">{{ item.firstName }} {{ item.surname }}</span>
            </div>
          </template>

          <!-- Email Column -->
          <template v-slot:item.email="{ item }">
            <span>{{ item.email || 'N/A' }}</span>
          </template>

          <!-- Phone Column -->
          <template v-slot:item.phone="{ item }">
            <span>{{ formatPhone(item.phone) }}</span>
          </template>

          <!-- Status Column -->
          <template v-slot:item.status="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              text-color="white"
              small
            >
              {{ item.status || 'Active' }}
            </v-chip>
          </template>

          <!-- Date Added Column -->
          <template v-slot:item.dateAdded="{ item }">
            <span>{{ formatDate(item.dateAdded) }}</span>
          </template>

          <!-- Actions Column -->
          <template v-slot:item.actions="{ item }">
            <v-tooltip text="View Details" location="top">
              <template v-slot:activator="{ props }">
                <v-btn
                  icon
                  size="small"
                  v-bind="props"
                  @click="viewUserDetails(item)"
                  color="#002060"
                >
                  <v-icon size="20">mdi-eye</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- No Users State -->
    <v-row v-if="!loading && users.length === 0" class="mt-4">
      <v-col cols="12">
        <div class="no-users-container text-center pa-8">
          <v-icon size="64" color="grey-lighten-1">mdi-account-off</v-icon>
          <h3 class="no-users-title mt-4">No Users Found</h3>
          <p class="no-users-message">There are currently no users registered in the Liv-Life portal.</p>
        </div>
      </v-col>
    </v-row>

    <!-- User Details Dialog -->
    <v-dialog v-model="showDetailsDialog" max-width="800">
      <v-card style="border-radius: 17px;">
        <v-card-title class="text-center" style="background-color: #002060; color: white; border-radius: 17px 17px 0 0;">
          <v-icon left>mdi-account-details</v-icon>
          User Details
        </v-card-title>

        <v-card-text class="pa-6" v-if="selectedUser">
          <v-row>
            <!-- Personal Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Personal Information</h3>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :model-value="selectedUser.firstName || 'N/A'"
                label="First Name"
                variant="solo"
                rounded
                flat
                readonly
                prepend-inner-icon="mdi-account"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :model-value="selectedUser.surname || 'N/A'"
                label="Surname"
                variant="solo"
                rounded
                flat
                readonly
                prepend-inner-icon="mdi-account-outline"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :model-value="selectedUser.idNumber || 'N/A'"
                label="ID Number"
                variant="solo"
                rounded
                flat
                readonly
                prepend-inner-icon="mdi-card-account-details"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :model-value="formatPhone(selectedUser.phone)"
                label="Phone"
                variant="solo"
                rounded
                flat
                readonly
                prepend-inner-icon="mdi-phone"
              />
            </v-col>

            <!-- Contact Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Contact Information</h3>
            </v-col>

            <v-col cols="12">
              <v-text-field
                :model-value="selectedUser.email || 'N/A'"
                label="Email"
                variant="solo"
                rounded
                flat
                readonly
                prepend-inner-icon="mdi-email"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                :model-value="selectedUser.address || 'N/A'"
                label="Address"
                variant="solo"
                rounded
                flat
                readonly
                prepend-inner-icon="mdi-home"
              />
            </v-col>

            <!-- System Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">System Information</h3>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :model-value="selectedUser.memberId || 'N/A'"
                label="Member ID"
                variant="solo"
                rounded
                flat
                readonly
                prepend-inner-icon="mdi-identifier"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :model-value="selectedUser.clientId || 'N/A'"
                label="Client ID"
                variant="solo"
                rounded
                flat
                readonly
                prepend-inner-icon="mdi-identifier"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :model-value="formatDate(selectedUser.dateAdded)"
                label="Date Added"
                variant="solo"
                rounded
                flat
                readonly
                prepend-inner-icon="mdi-calendar"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :model-value="selectedUser.status || 'Active'"
                label="Status"
                variant="solo"
                rounded
                flat
                readonly
                prepend-inner-icon="mdi-check-circle"
              />
            </v-col>

            <!-- Banking Information -->
            <v-col cols="12" v-if="selectedUser.bankName || selectedUser.bankAccountNumber">
              <h3 class="section-title mb-4">Banking Information</h3>
            </v-col>

            <v-col cols="12" md="6" v-if="selectedUser.bankName">
              <v-text-field
                :model-value="selectedUser.bankName || 'N/A'"
                label="Bank Name"
                variant="solo"
                rounded
                flat
                readonly
                prepend-inner-icon="mdi-bank"
              />
            </v-col>

            <v-col cols="12" md="6" v-if="selectedUser.bankAccountNumber">
              <v-text-field
                :model-value="selectedUser.bankAccountNumber || 'N/A'"
                label="Account Number"
                variant="solo"
                rounded
                flat
                readonly
                prepend-inner-icon="mdi-credit-card"
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="justify-center pb-4">
          <v-btn
            color="grey"
            dark
            rounded
            size="large"
            @click="showDetailsDialog = false"
          >
            <v-icon left>mdi-close</v-icon>
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import NotificationDialog from '../components/NotificationDialog.vue';
import { formatPhoneNumber } from '../utils/phoneFormatter';

// Liv-Life Firebase configuration
const livLifeConfig = {
  apiKey: "AIzaSyDN9HD4uuaaoqubUlYKKrQBHey1sd0OXzs",
  authDomain: "nonprd-dmm-clientportal.firebaseapp.com",
  projectId: "nonprd-dmm-clientportal",
  storageBucket: "nonprd-dmm-clientportal.firebasestorage.app",
  messagingSenderId: "754935803615",
  appId: "1:754935803615:web:644554915d9cad7739c36a"
};

// Initialize secondary Firebase app for Liv-Life
const livLifeApp = initializeApp(livLifeConfig, 'livLife');
const livLifeDb = getFirestore(livLifeApp);
const livLifeAuth = getAuth(livLifeApp);

export default {
  name: 'LivLifeUsers',
  components: {
    NotificationDialog
  },
  data() {
    return {
      users: [],
      loading: true,
      search: '',
      statusFilter: null,
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
      showDetailsDialog: false,
      selectedUser: null,
      headers: [
        { title: 'Member ID', key: 'memberId', sortable: true, align: 'center' },
        { title: 'Client ID', key: 'clientId', sortable: true, align: 'center' },
        { title: 'Name', key: 'name', sortable: true, align: 'center' },
        { title: 'Email', key: 'email', sortable: true, align: 'center' },
        { title: 'Phone', key: 'phone', sortable: true, align: 'center' },
        { title: 'Status', key: 'status', sortable: true, align: 'center' },
        { title: 'Date Added', key: 'dateAdded', sortable: true, align: 'center' },
        { title: 'Actions', key: 'actions', sortable: false, align: 'center' }
      ],
      statusOptions: [
        { title: 'All', value: null },
        { title: 'Active', value: 'Active' },
        { title: 'Inactive', value: 'Inactive' },
        { title: 'Pending', value: 'Pending' }
      ]
    };
  },
  computed: {
    filteredUsers() {
      let filtered = [...this.users];

      // Apply search filter
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(user => {
          const fullName = `${user.firstName} ${user.surname}`.toLowerCase();
          const email = (user.email || '').toLowerCase();
          const phone = (user.phone || '').toLowerCase();
          const memberId = (user.memberId || '').toLowerCase();
          const clientId = (user.clientId || '').toLowerCase();

          return fullName.includes(searchLower) ||
                 email.includes(searchLower) ||
                 phone.includes(searchLower) ||
                 memberId.includes(searchLower) ||
                 clientId.includes(searchLower);
        });
      }

      // Apply status filter
      if (this.statusFilter) {
        filtered = filtered.filter(user => {
          const status = user.status || 'Active';
          return status === this.statusFilter;
        });
      }

      return filtered;
    }
  },
  methods: {
    async loadUsers() {
      this.loading = true;
      try {
        // Load users from the Liv-Life 'users' collection
        const usersCollection = collection(livLifeDb, 'users');
        const usersSnapshot = await getDocs(usersCollection);
        
        this.users = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        console.log(`Loaded ${this.users.length} users from Liv-Life portal`);

        if (this.users.length === 0) {
          this.notificationType = 'info';
          this.notificationTitle = 'No Users';
          this.notificationMessage = 'No users found in the Liv-Life portal.';
          this.showNotification = true;
        }
      } catch (error) {
        console.error('Error loading Liv-Life users:', error);
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to load users from Liv-Life portal. Please try again.';
        this.showNotification = true;
      } finally {
        this.loading = false;
      }
    },

    viewUserDetails(user) {
      this.selectedUser = user;
      this.showDetailsDialog = true;
    },

    formatPhone(phone) {
      return formatPhoneNumber(phone) || 'N/A';
    },

    formatDate(dateValue) {
      if (!dateValue) return 'N/A';
      
      try {
        // Handle date object
        if (dateValue && typeof dateValue === 'object' && dateValue.month) {
          const date = new Date(dateValue.year, dateValue.month - 1, dateValue.day);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        }
        
        // Handle string date
        if (typeof dateValue === 'string') {
          const date = new Date(dateValue);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            });
          }
          return dateValue;
        }
        
        return 'N/A';
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'N/A';
      }
    },

    getStatusColor(status) {
      switch (status?.toLowerCase()) {
        case 'active':
          return 'success';
        case 'inactive':
          return 'error';
        case 'pending':
          return 'warning';
        default:
          return 'success';
      }
    }
  },

  async created() {
    await this.loadUsers();
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #002060;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.no-users-container {
  background: white;
  border-radius: 17px;
  border: 2px dashed #e5e7eb;
}

.no-users-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #002060;
}

.no-users-message {
  font-size: 1rem;
  color: #666;
  margin-top: 0.5rem;
}

:deep(.v-field--variant-solo) {
  background-color: #f8f9fa !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: #f8f9fa !important;
}
</style>